import { comparer, reaction, type IEqualsComparer } from "mobx";
import { useEffect, useLayoutEffect, useRef, useState, type DependencyList } from "react";

export interface UseMobxSnapshotOptions<T> {
  equals?: IEqualsComparer<T>;
}

/**
 * Subscribes to MobX observables inside `compute` and exposes their snapshot as
 * React state. The reaction is recreated whenever `dependencies` change.
 */
export function useMobxSnapshot<T>(
  compute: () => T,
  dependencies: DependencyList = [],
  options?: UseMobxSnapshotOptions<T>
): T {
  const [scope, setScope] = useState(() => ({ dependencies, version: 0 }));
  if (scope.dependencies.length !== dependencies.length ||
      scope.dependencies.some((value, index) => !Object.is(value, dependencies[index]))) {
    setScope({ dependencies, version: scope.version + 1 });
  }
  const computeRef = useRef(compute);
  const equalsRef = useRef(options?.equals);
  useLayoutEffect(() => {
    computeRef.current = compute;
    equalsRef.current = options?.equals;
  });

  const [snapshot, setSnapshot] = useState<T>(() => compute());

  useEffect(() => {
    let disposed = false;

    const dispose = reaction(
      () => computeRef.current(),
      (value) => {
        if (disposed) {
          return;
        }
        setSnapshot(value);
      },
      {
        fireImmediately: true,
        // Inline comparers may change identity every render without changing
        // the subscription scope. Recreating a fireImmediately reaction for
        // those identities would continuously publish fresh object snapshots.
        equals: (left, right) => (equalsRef.current ?? comparer.default)(left, right),
      }
    );

    return () => {
      disposed = true;
      dispose();
    };
  }, [scope.version]);

  return snapshot;
}
